/**
 * Marina-related models
 */

/**
 * Operating hours for a day
 */
export interface DayOperatingHours {
  open: string; // HH:mm format
  close: string; // HH:mm format
  isClosed: boolean;
}

/**
 * Operating hours for all days of the week
 */
export interface OperatingHours {
  [day: string]: DayOperatingHours; // e.g., "Monday", "Tuesday", etc.
}

/**
 * Marina DTO - Full marina information
 */
export interface MarinaDto {
  id: string;
  name: string;
  
  // Physical Address
  addressLine1: string;
  addressLine2?: string | null;
  addressCity: string;
  addressState: string;
  addressZip: string;
  addressCountry: string;
  
  // Contact Information
  email?: string | null;
  phone?: string | null;
  websiteUrl?: string | null;
  
  // Business Information
  taxId?: string | null; // EIN / VAT
  businessLicense?: string | null;
  timezone?: string | null; // e.g., "America/New_York"
  currency?: string | null; // e.g., "USD"
  logoUrl?: string | null;
  
  // Operations
  operatingHours?: OperatingHours | null;
  /** Number of days within which a contract must be signed. Null = no expiry. */
  contractSignatureExpiryDays?: number | null;
  /** Marina-level sales tax rate percentage used for POS and launch billing. */
  salesTaxRate?: number | null;
  /** Fuel list subtotal required before POS offers a staff $/gal price-off. */
  posFuelDiscountThresholdAmount?: number | null;
  /** Maximum dollars off per gallon on non–Boat Club POS fuel. */
  posFuelMaxDiscountPerGallon?: number | null;
  /** Default payment terms on invoices/PDFs for this marina (e.g. Net 30). */
  invoicePaymentTerms?: string | null;
  /**
   * Stripe Terminal Location ID (loc_...).
   * Scopes POS reader discovery; a marina may have multiple readers at this location.
   */
  stripeTerminalLocationId?: string | null;

  // Metadata
  isPrimary?: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
}

/**
 * Weather DTO - Current weather for a marina (temperature in Fahrenheit, wind in knots)
 */
export interface WeatherDto {
  temperatureF: number;
  condition: string;
  windSpeedKnots: number;
  windDirection: string;
  precipitationProbability: number;
  /** City where weather data is taken from */
  locationCity: string;
  /** Country where weather data is taken from */
  locationCountry: string;
  /** Marina name */
  marinaName: string;
  /** Marina city (from address) */
  marinaCity: string;
  /** Marina country (from address) */
  marinaCountry: string;
}

/**
 * Create Marina Request
 */
export interface CreateMarinaRequest {
  name: string;
  addressLine1: string;
  addressLine2?: string | null;
  addressCity: string;
  addressState: string;
  addressZip: string;
  addressCountry: string;
  email?: string | null;
  phone?: string | null;
  websiteUrl?: string | null;
  taxId?: string | null;
  businessLicense?: string | null;
  timezone?: string | null;
  currency?: string | null;
  operatingHours?: OperatingHours | null;
  contractSignatureExpiryDays?: number | null;
  salesTaxRate?: number | null;
  posFuelDiscountThresholdAmount?: number | null;
  posFuelMaxDiscountPerGallon?: number | null;
  invoicePaymentTerms?: string | null;
  stripeTerminalLocationId?: string | null;
}

/**
 * Update Marina Request
 */
export interface UpdateMarinaRequest {
  name?: string;
  addressLine1?: string;
  addressLine2?: string | null;
  addressCity?: string;
  addressState?: string;
  addressZip?: string;
  addressCountry?: string;
  email?: string | null;
  phone?: string | null;
  websiteUrl?: string | null;
  taxId?: string | null;
  businessLicense?: string | null;
  timezone?: string | null;
  currency?: string | null;
  operatingHours?: OperatingHours | null;
  contractSignatureExpiryDays?: number | null;
  salesTaxRate?: number | null;
  posFuelDiscountThresholdAmount?: number | null;
  posFuelMaxDiscountPerGallon?: number | null;
  invoicePaymentTerms?: string | null;
  stripeTerminalLocationId?: string | null;
}

